import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Ship, Clock, Award, MapPin, PiggyBank, PlusCircle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const cur = (c) => (c === "USD" ? "US$" : c === "EUR" ? "€" : `${c} `);
const money = (n, c) => (n == null ? "On request" : `${cur(c)}${Number(n).toLocaleString()}`);
const aud = (n) => (n == null ? "—" : `A$${Math.round(n).toLocaleString()}`);
const relColor = (v) => (v >= 80 ? "#059669" : v >= 60 ? "#0B2B5C" : v >= 45 ? "#D97706" : "#DC2626");

const LandedCost = ({ route, data }) => {
  if (!route || route.sgl_offer == null || !data?.locals || !data?.fx) return null;
  const { locals, fx } = data;
  const best = route.carriers.find((c) => c.rate != null && c.rate === route.sgl_offer) || route.carriers[0];
  const thc = locals.thc_by_carrier[best?.carrier] ?? 895;
  const cartage = locals.cartage?.[0] || { label: "Cartage", amount: 0 };
  const perCont = (locals.per_container || []).reduce((a, x) => a + x.amount, 0);
  const perShip = (locals.per_shipment || []).reduce((a, x) => a + x.amount, 0);
  const ocean = Math.round(route.sgl_offer * fx.rate);
  const fuel = Math.round(ocean * locals.fuel_surcharge_pct / 100);
  const localsTotal = thc + perCont + perShip + cartage.amount;
  const total = ocean + fuel + localsTotal;
  const Row = ({ label, val, strong }) => (
    <div className={`flex items-center justify-between py-1.5 ${strong ? "font-semibold" : ""}`}>
      <span className={strong ? "text-foreground" : "text-muted-foreground"}>{label}</span>
      <span className="tabular-nums">{val}</span>
    </div>
  );
  return (
    <div className="mt-10" data-testid="ratecard-landed-cost">
      <h3 className="text-base font-semibold tracking-tight mb-1">Estimated landed cost · {route.destination}</h3>
      <p className="text-xs text-muted-foreground mb-4">SGL offer converted at OANDA {fx.pair} {fx.rate}{fx.as_of ? ` (${fx.as_of})` : ""}, plus origin local charges. Indicative — fuel surcharge fluctuates.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-border bg-white p-6 text-sm">
          <div className="font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground mb-3">Origin local charges (AUD)</div>
          <Row label={`Terminal Handling — ${best?.carrier}`} val={aud(thc)} />
          {locals.per_container.map((x, i) => <Row key={`c${i}`} label={x.label} val={aud(x.amount)} />)}
          {locals.per_shipment.map((x, i) => <Row key={`s${i}`} label={x.label} val={aud(x.amount)} />)}
          <Row label={cartage.label} val={aud(cartage.amount)} />
          <div className="border-t border-border mt-2 pt-2"><Row label="Local charges subtotal" val={aud(localsTotal)} strong /></div>
          {locals.cartage?.[1] && <p className="text-[11px] text-muted-foreground/70 mt-2">Alt cartage: {locals.cartage[1].label} {aud(locals.cartage[1].amount)}</p>}
        </div>
        <div className="border-2 border-[#0B2B5C] bg-[#0B2B5C]/[0.03] p-6 text-sm flex flex-col lg:self-start" data-testid="ratecard-total">
          <div className="font-mono-plex text-[10px] tracking-widest uppercase text-[#0B2B5C] mb-3 font-semibold">Estimated total (AUD)</div>
          <Row label={`Ocean freight (${money(route.sgl_offer, route.currency)} @ ${fx.rate})`} val={aud(ocean)} />
          <Row label={`Fuel surcharge (${locals.fuel_surcharge_pct}%)`} val={aud(fuel)} />
          <Row label="Origin local charges" val={aud(localsTotal)} />
          <div className="border-t-2 border-[#0B2B5C]/30 mt-3 pt-3 flex items-end justify-between">
            <span className="font-mono-plex text-[11px] tracking-widest uppercase text-muted-foreground">Total est.</span>
            <span className="font-display text-3xl tracking-tight text-foreground tabular-nums" data-testid="ratecard-total-value">{aud(total)}</span>
          </div>
          <p className="text-[11px] text-muted-foreground/70 mt-3">Excludes duties, taxes, quarantine, fumigation & any government/third-party fees. Customs clearance includes 5 entry lines.</p>
        </div>
      </div>
    </div>
  );
};

const monthLabel = (m) => {
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1).toLocaleString("en", { month: "short" }) + " " + y;
};

const CostAvoidance = ({ route, data, onAdd, adding }) => {
  const priced = route.carriers.filter((c) => c.rate != null);
  if (route.sgl_offer == null || priced.length < 2) return null;
  const maxC = priced.reduce((a, b) => (b.rate > a.rate ? b : a));
  const savingUsd = maxC.rate - route.sgl_offer;
  const savingAud = Math.round(savingUsd * (data.fx?.rate || 1));
  const pct = maxC.rate ? Math.round((savingUsd / maxC.rate) * 100) : 0;
  return (
    <div className="border border-emerald-200 bg-emerald-50/60 p-6 mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5" data-testid="ratecard-cost-avoidance">
      <div>
        <div className="flex items-center gap-2 text-emerald-700 mb-2"><PiggyBank className="h-4 w-4" /><span className="font-mono-plex text-[11px] tracking-widest uppercase font-semibold">Cost Avoidance</span></div>
        <div className="text-sm text-muted-foreground">Most expensive <span className="text-foreground font-medium">{maxC.carrier} {money(maxC.rate, route.currency)}</span> <span className="mx-1">→</span> SGL offer <span className="text-foreground font-medium">{money(route.sgl_offer, route.currency)}</span></div>
      </div>
      <div className="flex items-center gap-6">
        <div className="text-right sm:text-left">
          <div className="font-display text-3xl tracking-tight text-emerald-700" data-testid="cost-avoidance-saving">{money(savingUsd, route.currency)}</div>
          <div className="text-xs text-muted-foreground">saved / container · {aud(savingAud)} · {pct}% below peak</div>
        </div>
        <button onClick={onAdd} disabled={adding} data-testid="add-cost-avoidance-button" className="shrink-0 inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-3 text-xs font-mono-plex tracking-widest uppercase rounded-md hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          <PlusCircle className="h-4 w-4" />{adding ? "Adding…" : "Add to tracker"}
        </button>
      </div>
    </div>
  );
};

const Tracker = ({ tracker }) => {
  if (!tracker) return null;
  const rows = tracker.monthly.map((m) => ({ ...m, label: monthLabel(m.month) }));
  const th = "px-4 py-3 font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground font-semibold";
  return (
    <div className="mt-14 pt-10 border-t border-border" data-testid="cost-avoidance-tracker">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-6 gap-4">
        <div>
          <h3 className="font-display text-2xl tracking-tight">Cost Avoidance Tracker</h3>
          <p className="text-sm text-muted-foreground mt-1">Cumulative savings vs the most expensive carrier, month on month.</p>
        </div>
        <div className="text-left sm:text-right">
          <div className="font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground">Cumulative saved</div>
          <div className="font-display text-4xl tracking-tight text-emerald-700" data-testid="cost-avoidance-total">{aud(tracker.total_aud)}</div>
          <div className="text-xs text-muted-foreground">{money(tracker.total_usd, "USD")} · {tracker.count} shipments</div>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="tracker-empty">No entries yet. Use “Add to tracker” on a lane above to start tracking month-on-month savings.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-border bg-white p-6">
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={rows} margin={{ top: 5, right: 10, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="label" stroke="#64748B" fontSize={11} tickLine={false} axisLine={{ stroke: "#CBD5E1" }} />
                <YAxis stroke="#64748B" fontSize={11} axisLine={false} tickFormatter={(v) => (v >= 1000 ? `A$${(v / 1000).toFixed(1)}k` : `A$${v}`)} />
                <Tooltip formatter={(v) => `A$${Math.round(v).toLocaleString()}`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar name="Monthly saved" dataKey="saving_aud" fill="#059669" radius={[3, 3, 0, 0]} barSize={38} />
                <Line name="Cumulative" type="monotone" dataKey="cumulative_aud" stroke="#0B2B5C" strokeWidth={2.5} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left">
                <th className={th}>Month</th>
                <th className={`${th} text-right`}>Shipments</th>
                <th className={`${th} text-right`}>Saved (AUD)</th>
                <th className={`${th} text-right`}>Cumulative</th>
              </tr></thead>
              <tbody>
                {rows.map((m, i) => (
                  <tr key={i} className="border-t border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`tracker-month-${i}`}>
                    <td className="px-4 py-2.5 font-medium">{m.label}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">{m.count}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{aud(m.saving_aud)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-emerald-700">{aud(m.cumulative_aud)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default function RateCard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [equipment, setEquipment] = useState("");
  const [tracker, setTracker] = useState(null);
  const [adding, setAdding] = useState(false);

  const loadTracker = () => api.get("/commercial/cost-avoidance").then((r) => setTracker(r.data)).catch(() => {});

  useEffect(() => { loadTracker(); }, []);

  useEffect(() => {
    api.get("/commercial/rate-card").then((r) => {
      setData(r.data);
      const routes = r.data.routes;
      if (routes.length) {
        setOrigin(routes[0].origin);
        setDestination(routes[0].destination);
        setEquipment(r.data.equipment_types[0]);
      }
    }).catch(() => setError(true));
  }, []);

  const origins = useMemo(() => (data ? [...new Set(data.routes.map((r) => r.origin))] : []), [data]);
  const destinations = useMemo(
    () => (data ? data.routes.filter((r) => r.origin === origin).map((r) => r.destination) : []),
    [data, origin]
  );

  const onOrigin = (v) => {
    setOrigin(v);
    const first = data.routes.find((r) => r.origin === v);
    if (first) setDestination(first.destination);
  };

  const route = data?.routes.find((r) => r.origin === origin && r.destination === destination);

  const addAvoidance = async () => {
    if (!route) return;
    const priced = route.carriers.filter((c) => c.rate != null);
    if (route.sgl_offer == null || priced.length < 2) return;
    const maxC = priced.reduce((a, b) => (b.rate > a.rate ? b : a));
    const best = route.carriers.find((c) => c.rate === route.sgl_offer) || priced.reduce((a, b) => (b.rate < a.rate ? b : a));
    setAdding(true);
    try {
      await api.post("/commercial/cost-avoidance", {
        origin: route.origin, destination: route.destination, equipment: route.equipment,
        most_expensive_carrier: maxC.carrier, most_expensive_usd: maxC.rate,
        sgl_carrier: best.carrier, sgl_usd: route.sgl_offer, quantity: 1,
      });
      toast.success("Added to cost avoidance tracker", { description: `${route.origin} → ${route.destination}` });
      loadTracker();
    } catch {
      toast.error("Could not add to tracker");
    } finally {
      setAdding(false);
    }
  };

  if (error) return <div className="border border-border bg-white p-10 text-center text-sm text-muted-foreground" data-testid="ratecard-error">Unable to load the rate card. Please try again.</div>;
  if (!data) return <div className="border border-border bg-white p-16 text-center font-mono-plex text-xs tracking-widest uppercase text-muted-foreground animate-pulse" data-testid="ratecard-loading">Loading rate card…</div>;

  return (
    <div data-testid="rate-card">
      <p className="text-sm text-muted-foreground max-w-3xl mb-6">{data.meta.subtitle}</p>

      {/* Lookup controls */}
      <div className="border border-border bg-white p-5 grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" data-testid="ratecard-lookup">
        <div>
          <label className="block font-mono-plex text-[11px] tracking-widest uppercase text-muted-foreground mb-2">Origin</label>
          <Select value={origin} onValueChange={onOrigin}>
            <SelectTrigger data-testid="ratecard-origin" className="w-full bg-white border border-border rounded-md"><SelectValue /></SelectTrigger>
            <SelectContent>{origins.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="block font-mono-plex text-[11px] tracking-widest uppercase text-muted-foreground mb-2">Destination</label>
          <Select value={destination} onValueChange={setDestination}>
            <SelectTrigger data-testid="ratecard-destination" className="w-full bg-white border border-border rounded-md"><SelectValue /></SelectTrigger>
            <SelectContent>{destinations.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="block font-mono-plex text-[11px] tracking-widest uppercase text-muted-foreground mb-2">Equipment</label>
          <Select value={equipment} onValueChange={setEquipment}>
            <SelectTrigger data-testid="ratecard-equipment" className="w-full bg-white border border-border rounded-md"><SelectValue /></SelectTrigger>
            <SelectContent>{data.equipment_types.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {!route ? (
        <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="ratecard-noresult">No rate on file for this port pair. Submit a quotation request for a tailored offer.</div>
      ) : (
        <div data-testid="ratecard-result">
          {/* Route header + SGL most competitive */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-2 border border-border bg-white p-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-3"><MapPin className="h-4 w-4" strokeWidth={1.5} /><span className="font-mono-plex text-[11px] tracking-widest uppercase">Port pair · {route.equipment}</span></div>
              <div className="font-display text-2xl sm:text-3xl tracking-tight">{route.origin} <span className="text-muted-foreground">→</span> {route.destination}</div>
              <div className="mt-3 text-xs text-muted-foreground font-mono-plex">Validity {route.validity || "—"}{route.volume ? ` · Volume ${route.volume}` : ""} · Rates in {route.currency}</div>
            </div>
            <div className="border-2 border-[#E3000F] bg-[#E3000F]/[0.04] p-6 flex flex-col justify-center" data-testid="ratecard-sgl-offer">
              <div className="flex items-center gap-2 text-[#E3000F] mb-2"><Award className="h-4 w-4" /><span className="font-mono-plex text-[11px] tracking-widest uppercase font-semibold">SGL Most Competitive Offer</span></div>
              <div className="font-display text-4xl tracking-tight text-foreground">{money(route.sgl_offer, route.currency)}</div>
              <div className="text-xs text-muted-foreground mt-1">per {route.equipment}</div>
            </div>
          </div>

          <CostAvoidance route={route} data={data} onAdd={addAvoidance} adding={adding} />

          {/* Carrier comparison */}
          <h3 className="text-base font-semibold tracking-tight mb-1">Carrier rates for this lane</h3>
          <p className="text-xs text-muted-foreground mb-4">Associated rate, transit time &amp; service per carrier · best offer highlighted · schedule reliability matched by service name from the Exports briefing (Sea-Intelligence), blank where no service match</p>
          <div className="border border-border overflow-x-auto">
            <TooltipProvider delayDuration={100}>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="px-4 py-3 font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground font-semibold">Carrier</th>
                  <th className="px-4 py-3 font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground font-semibold text-right">Rate ({route.equipment})</th>
                  <th className="px-4 py-3 font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground font-semibold">Transit</th>
                  <th className="px-4 py-3 font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground font-semibold">Service details</th>
                  <th className="px-4 py-3 font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground font-semibold text-right">Schedule Reliability</th>
                </tr>
              </thead>
              <tbody>
                {route.carriers.map((c, i) => {
                  const best = c.rate != null && route.sgl_offer != null && c.rate === route.sgl_offer;
                  return (
                    <tr key={i} className={`border-t border-slate-100 transition-colors ${best ? "bg-[#E3000F]/[0.05]" : "hover:bg-slate-50"}`} data-testid={`ratecard-carrier-${i}`}>
                      <td className="px-4 py-3 font-medium">
                        <span className="inline-flex items-center gap-1.5"><Ship className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />{c.carrier}
                          {best && <span className="ml-1 font-mono-plex text-[9px] tracking-widest uppercase text-white bg-[#E3000F] px-1.5 py-0.5 rounded">Best</span>}</span>
                      </td>
                      <td className={`px-4 py-3 text-right tabular-nums font-semibold ${best ? "text-[#E3000F]" : "text-foreground"}`}>{money(c.rate, route.currency)}</td>
                      <td className="px-4 py-3 whitespace-nowrap"><span className="inline-flex items-center gap-1.5 text-muted-foreground"><Clock className="h-3.5 w-3.5" strokeWidth={1.5} />{c.transit || "—"}</span></td>
                      <td className="px-4 py-3 text-muted-foreground">{c.service || "—"}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold" style={c.reliability != null ? { color: relColor(c.reliability) } : undefined}>
                        {c.reliability != null ? (
                          <UITooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help underline decoration-dotted decoration-current/40 underline-offset-4">{c.reliability.toFixed(1)}%</span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[280px] text-left">
                              <div className="text-[11px] leading-snug">
                                <div className="font-semibold mb-0.5">{c.reliability_source?.lane}</div>
                                <div className="opacity-80">Service: {c.reliability_source?.service}</div>
                                <div className="opacity-70 mt-1">Matched on “{c.reliability_source?.matched}” · Sea-Intelligence Exports briefing</div>
                              </div>
                            </TooltipContent>
                          </UITooltip>
                        ) : <span className="text-muted-foreground/40 font-normal">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </TooltipProvider>
          </div>
          <p className="text-[11px] text-muted-foreground/70 mt-4 leading-relaxed max-w-4xl">{data.meta.note} Issued {data.meta.issued} · {data.meta.provider} · {data.meta.contact}</p>

          <LandedCost route={route} data={data} />
        </div>
      )}

      <Tracker tracker={tracker} />
    </div>
  );
}
